import styles from './CustomerModule.module.scss';
import { Outlet } from 'react-router-dom';
import { clsxm } from '@app/styles/clsxm';
import { Header } from '@app/layout/header/Header';
import { CustomerSidebarMenu } from '@app/layout/customer-sidebar-menu/CustomerSidebarMenu';
import { useStore } from '@app/store/useStore.hook';
import { useActivePlan } from '@app/domain/plan/hooks/useActivePlan';
import { TrialExpiredPage } from './pages/trial/TrialExpiredPage';
import { BlackFridayBanner } from './components/BlackFridayBanner';
import { GlobalWarningBanner } from './components/GlobalWarningBanner';
import { MaybeAutoClaim } from './components/MaybeAutoClaim';
import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { OnboardingDialog } from './components/onboarding/OnboardingDialog';
import { useEffect, useState } from 'react';
import { useIsMobile } from '@app/hooks/useIsMobile.hook';
import { useTopBannerState } from './components/top-banner/atoms/topBanner';
import { TopBanner } from './components/top-banner/TopBanner';
import { EOnboardingModalType, useTrialPhasesConfig } from '@app/domain/plan/hooks/useTrialPhasesConfig';
import { ETrialOnboardingType, useTrialOnboardingDialog } from './components/onboarding/TrialOnboardingDialog';
import { useHandler } from '@app/hooks/useHandler.hook';

export const CustomerModule: React.FC = () => {
  const { isDesktop } = useStore.useCurrentBreakpointDevice();
  const isMobile = useIsMobile();
  const { height, isActive: isShowTopBanner } = useTopBannerState();

  const isExpiredTrialInOldGenUsers = false; // isTrial && isExpired && isOldPlanUser;

  const outlet = isExpiredTrialInOldGenUsers ? <TrialExpiredPage /> : <Outlet />;

  return (
    <div className={clsxm('relative flex w-full flex-1 bg-accept-light-bg', styles.root)}>
      <Header />
      <div className={styles['app-content']}>
        <CustomerSidebarMenu />
        <main className={clsxm('w-full pb-20', isDesktop && 'pl-[260px]')}>
          <div
            style={{ paddingTop: !isMobile && isShowTopBanner ? `${height}px` : 0 }}
            className="flex flex-col gap-4 h-full"
          >
            <MaybeAutoClaim />
            {isMobile && isShowTopBanner && <TopBanner />}

            <BlackFridayBanner />
            <GlobalWarningBanner />
            <MaybeOnboarding />
            {outlet}
          </div>
        </main>
      </div>
    </div>
  );
};

const MaybeOnboarding: React.FC = () => {
  const { shouldShowOnboarding } = useAuthorizedUser();
  const [isOpen, setIsOpen] = useState(shouldShowOnboarding);
  const [resetKey, setResetKey] = useState(0);
  useShowTrialOnboarding();

  useEffect(() => {
    if (shouldShowOnboarding) {
      setResetKey((i) => i + 1);
      setIsOpen(true);
    }
  }, [shouldShowOnboarding]);

  return (
    <OnboardingDialog
      key={resetKey}
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
    />
  );
};

const SHOULD_SHOW_ACTIVATION_FOCUS_ONBOARDING_DIALOGS = 4;

export const useShowTrialOnboarding = () => {
  const { openTrialOnboardingDialog, removeTrialOnboardingDialog } = useTrialOnboardingDialog(null);
  const { shouldShowOnboarding, currentUser } = useAuthorizedUser();
  const { isTrial, isPlanUnderChange, daysSinceStart, isExperimentGroupB } = useActivePlan();
  const { modalType } = useTrialPhasesConfig();

  const shouldSkipScanCongratulationModal = useHandler((): boolean => {
    const isWithinActivationPeriod = (daysSinceStart || 0) <= SHOULD_SHOW_ACTIVATION_FOCUS_ONBOARDING_DIALOGS;
    const isScanCongratsModal = modalType === EOnboardingModalType.SCAN_CONGRATULATION;

    return Boolean(isScanCongratsModal && (!isWithinActivationPeriod || currentUser?.isFirstScanSeen));
  });

  useEffect(() => {
    if (shouldShowOnboarding || isPlanUnderChange || !isTrial || shouldSkipScanCongratulationModal()) {
      return;
    }
    if (modalType !== EOnboardingModalType.NONE) {
      openTrialOnboardingDialog(ETrialOnboardingType.TRIAL_ONBOADRDING);
    }
    return () => {
      removeTrialOnboardingDialog();
    };
  }, [
    shouldShowOnboarding,
    openTrialOnboardingDialog,
    isTrial,
    isExperimentGroupB,
    modalType,
    removeTrialOnboardingDialog,
    isPlanUnderChange,
    daysSinceStart,
    currentUser?.isFirstScanSeen,
    shouldSkipScanCongratulationModal,
  ]);
};
